import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import type { Attachment } from '../../types/project'
import type { PartySnapshot } from '../../types/tag'
import type { User } from '../../types/user'
import ContactInfoDisplay from '../ContactInfoDisplay'
import NoteAttachments from '../NoteAttachments'
import OpenMap from '../OpenMaps'

interface TagConfirmationProps {
  data: {
    title?: string
    origin_name?: string
    origin_address?: string
    destination_address?: string
    material?: string
    rate_type?: string
    pay_rate?: string
    notes?: string
    start_date?: string
    assigned_user_id?: number
    assigned_user?: User
    shared_emails?: string[]
    contractor?: string
    contractor_user?: User
    broker?: string
    broker_user?: User
    project_label?: string
    workorder_label?: string
    tag_label?: string
    // Snapshot data (immutable historical data)
    contractor_snapshot?: PartySnapshot
    broker_snapshot?: PartySnapshot
    sub_hauler_snapshot?: PartySnapshot
    dispatcher_snapshot?: PartySnapshot
    assigned_driver_snapshot?: PartySnapshot
    // Role of who created/sent the dispatch
    dispatch_created_by_role?: string
  }
  isSuccess?: boolean
  attachments?: Attachment[]
  pendingAttachments?: File[]
}

// Helper component to render contact info from snapshot or user
const ContactField: React.FC<{
  label: string
  snapshot?: PartySnapshot
  user?: User
  fallbackName?: string
}> = ({ label, snapshot, user, fallbackName }) => {
  const renderSnapshot = (snap: PartySnapshot) => (
    <div className={css(styles.snapshotInfo)}>
      <span className={css(styles.value)}>
        {snap.first_name} {snap.last_name}
      </span>
      {snap.email && (
        <a href={`mailto:${snap.email}`} className={css(styles.link)}>
          {snap.email}
        </a>
      )}
      {snap.phone && (
        <a href={`tel:${snap.phone}`} className={css(styles.link)}>
          {snap.phone}
        </a>
      )}
    </div>
  )

  return (
    <div className={css(styles.field)}>
      <span className={css(styles.label)}>{label}</span>
      {snapshot ? (
        renderSnapshot(snapshot)
      ) : user ? (
        <ContactInfoDisplay user={user} variant="inline" />
      ) : (
        <span className={css(styles.value)}>{fallbackName || 'None'}</span>
      )}
    </div>
  )
}

const TagConfirmation: React.FC<TagConfirmationProps> = ({
  data,
  isSuccess = false,
  attachments = [],
  pendingAttachments = [],
}) => {
  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'None'
    const date = new Date(dateString)
    const dateStr = date.toLocaleDateString('en-US')
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    return `${dateStr} at ${timeStr}`
  }

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>
        {isSuccess ? 'TAG SENT SUCCESSFULLY' : 'TAG CONFIRMATION'}
      </h3>

      {isSuccess && data.tag_label && (
        <div className={css(styles.successBanner)}>
          <span className={css(styles.successLabel)}>Tag ID</span>
          <span className={css(styles.successValue)}>{data.tag_label}</span>
        </div>
      )}

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Contact Information</h4>
        <div className={css(styles.grid)}>
          <ContactField
            label="Assigned Driver"
            snapshot={data.assigned_driver_snapshot}
            user={data.assigned_user}
          />
          <ContactField
            label="Contractor"
            snapshot={data.contractor_snapshot}
            user={data.contractor_user}
            fallbackName={data.contractor}
          />
          <ContactField
            label={
              data.dispatch_created_by_role === 'BROKER'
                ? 'Sent from Broker'
                : 'Broker'
            }
            snapshot={data.broker_snapshot}
            user={data.broker_user}
            fallbackName={data.broker}
          />
          <ContactField
            label="Sub Hauler"
            snapshot={data.sub_hauler_snapshot}
          />
          <ContactField
            label="Dispatcher"
            snapshot={data.dispatcher_snapshot}
          />
          {data.shared_emails && data.shared_emails.length > 0 && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Shared With</span>
              <span className={css(styles.value)}>
                {data.shared_emails.join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className={css(styles.section)}>
        <h4 className={css(styles.subTitle)}>Job Details</h4>
        <div className={css(styles.grid)}>
          {data.project_label && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Project ID</span>
              <span className={css(styles.value)}>{data.project_label}</span>
            </div>
          )}
          {data.workorder_label && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Dispatch ID</span>
              <span className={css(styles.value)}>{data.workorder_label}</span>
            </div>
          )}
          {data.tag_label && (
            <div className={css(styles.field)}>
              <span className={css(styles.label)}>Tag ID</span>
              <span className={css(styles.value)}>{data.tag_label}</span>
            </div>
          )}
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Title</span>
            <span className={css(styles.value)}>{data.title || 'None'}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Site Name</span>
            <span className={css(styles.value)}>
              {data.origin_name || 'None'}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Pick Up Location</span>
            <span className={css(styles.value)}>
              {data.origin_address ? (
                <OpenMap address={data.origin_address} />
              ) : (
                'None'
              )}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Drop Off Location</span>
            <span className={css(styles.value)}>
              {data.destination_address ? (
                <OpenMap address={data.destination_address} />
              ) : (
                'None'
              )}
            </span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Material</span>
            <span className={css(styles.value)}>{data.material || 'None'}</span>
          </div>
          <div className={css(styles.field)}>
            <span className={css(styles.label)}>Start Date & Time</span>
            <span className={css(styles.value)}>
              {formatDateTime(data.start_date)}
            </span>
          </div>
        </div>
      </div>

      {data.notes && (
        <div className={css(styles.section)}>
          <h4 className={css(styles.subTitle)}>Notes</h4>
          <div className={css(styles.notes)}>{data.notes}</div>
        </div>
      )}

      {(attachments.length > 0 || pendingAttachments.length > 0) && (
        <div className={css(styles.section)}>
          <h4 className={css(styles.subTitle)}>Attachments</h4>
          <NoteAttachments
            attachments={attachments}
            pendingFiles={pendingAttachments}
            onPendingFilesChange={() => {}}
            readOnly={true}
          />
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '24px',
  },
  successBanner: {
    backgroundColor: '#ECFDF5',
    border: '1px solid #10B981',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  successLabel: {
    color: '#065F46',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  successValue: {
    color: '#065F46',
    fontSize: '20px',
    fontWeight: 700,
  },
  subTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    color: '#6B7280',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
    lineHeight: '1.4',
  },
  value: {
    color: '#111827',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '1.4',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  snapshotInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  subValue: {
    color: '#6B7280',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '1.4',
  },
  link: {
    color: '#2563EB',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '1.4',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  notes: {
    backgroundColor: '#F9FAFB',
    padding: '12px',
    borderRadius: '6px',
    fontSize: '13px',
    lineHeight: '1.5',
    color: '#374151',
  },
})

export default TagConfirmation
